import React, { useCallback, useMemo, useState } from 'react';
import { LessonsTabContent, LessonsTabContentSize, useDeviceSize } from '@atlassian/learning-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useParams } from 'react-router-dom';
import { css } from '@compiled/react';

import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { ContentfulLessonTabContent } from 'src/graphql/queries/GetSmLesson';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { MlLayoutContent } from 'src/graphql/generated/graphql';

interface LessonsTabWrapperProps {
  block: ContentfulLessonTabContent;
}

export const LessonsTabWrapperComponent: React.FC<LessonsTabWrapperProps> = ({ block }) => {
  const { user } = useAtlassianUser();
  const { courseId, lessonId } = useParams();
  const lessonsTabComponent = block;

  const [activeTab, setActiveTab] = useState(0);
  const contentLength = useMemo(() => lessonsTabComponent.contentCollection.items.length, [lessonsTabComponent]);
  const activeContent = useMemo(
    () =>
      activeTab < contentLength
        ? lessonsTabComponent.contentCollection.items[activeTab]
        : lessonsTabComponent.contentCollection.items[contentLength - 1],
    [activeTab, contentLength, lessonsTabComponent.contentCollection.items],
  );
  const width = useDeviceSize();
  const size = lessonsTabComponent.size && lessonsTabComponent.size.toLowerCase();

  const isStandaloneLesson = isStandalone();

  const onChangeTab = useCallback(
    (index: number) => {
      sendUIEvent(
        'lessonScreen',
        'tab',
        'clicked',
        'lessonTabComponent',
        {
          name: lessonsTabComponent.name,
          lessonSlug: lessonId,
          courseSlug: courseId,
          tabIndex: index,
          totalTabs: lessonsTabComponent.contentCollection.items.length,
          isStandaloneLesson: isStandaloneLesson,
        },
        user?.account_id,
      );

      setActiveTab(index);
    },
    [user, lessonsTabComponent, lessonId, courseId, isStandaloneLesson],
  );

  return (
    <div css={lessonTabContainerStyles}>
      <LessonsTabContent
        activeTab={activeTab}
        tabLinks={lessonsTabComponent.tabsLabel}
        onClickTab={onChangeTab}
        size={size as LessonsTabContentSize}
      >
        <div key={activeTab}>
          {(activeContent as MlLayoutContent)?.content.json &&
            documentToReactComponents(
              (activeContent as MlLayoutContent)?.content.json,
              getOptions((activeContent as MlLayoutContent)?.content, width),
            )}
        </div>
      </LessonsTabContent>
    </div>
  );
};

const lessonTabContainerStyles = css({
  width: '100%',
  margin: '40px 0',
  display: 'flex',
  justifyContent: 'center',
});
